import React from 'react';
import { interpolateRatingColor } from '../hooks/useCoffeeShops';

interface MapPinProps {
    rating: number;
    size?: number;
    selected?: boolean;
}

// simple colored circle with coffee icon
const MapPin: React.FC<MapPinProps> = ({ rating, size = 28, selected = false }) => {
    const color = interpolateRatingColor(rating);
    const borderWidth = selected ? 3 : 2;

    return (
        <div
            style={{
                width: size,
                height: size,
                backgroundColor: color,
                border: `${borderWidth}px solid white`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: selected
                    ? '0 3px 8px rgba(0,0,0,0.4)'
                    : '0 2px 4px rgba(0,0,0,0.3)',
                transform: selected ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
            }}
        >
            <svg
                width={size * 0.5}
                height={size * 0.5}
                viewBox="0 0 24 24"
                fill="white"
            >
                {/* mug-hot icon path */}
                <path d="M2 8h2v9a3 3 0 003 3h6a3 3 0 003-3v-9h2a2 2 0 002-2v-2a2 2 0 00-2-2h-2V1H6v3H4a2 2 0 00-2 2v2a2 2 0 002 2zm2-2h2v2H4V6zm14 0h2v2h-2V6zM8 3h8v1H8V3zm0 5h8v9a1 1 0 01-1 1H9a1 1 0 01-1-1V8z"/>
            </svg>
        </div>
    );
};

// generate data URL for use as Leaflet icon - simple circle with coffee icon
export function createMapPinIcon(rating: number, selected: boolean = false): string {
    const color = interpolateRatingColor(rating);
    const size = selected ? 32 : 28;
    const borderWidth = selected ? 3 : 2;

    // simpler svg with circle background and coffee mug icon
    const svg = `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - borderWidth/2}" fill="${color}" stroke="white" stroke-width="${borderWidth}"/>
            <g transform="translate(${size * 0.25}, ${size * 0.25}) scale(${size * 0.02})">
                <path fill="white" d="M18.5 3H6c-1.1 0-2 .9-2 2v5.71c0 3.83 2.95 7.18 6.78 7.29 3.96.12 7.22-3.06 7.22-7v-1h.5c1.93 0 3.5-1.57 3.5-3.5S20.43 3 18.5 3zm0 5H18V5h.5c.83 0 1.5.67 1.5 1.5S19.33 8 18.5 8z"/>
            </g>
        </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
}

// create cluster icon for marker clustering
export function createClusterIcon(count: number): string {
    const size = Math.min(50, 30 + Math.log(count) * 5);
    const color = count > 10 ? '#2bb941' : count > 5 ? '#f49f0c' : '#e54343';

    const svg = `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="${color}" stroke="white" stroke-width="2" opacity="0.9"/>
            <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 6}" fill="white" opacity="0.3"/>
            <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="middle" fill="white" font-size="${size * 0.35}" font-weight="bold" font-family="system-ui">${count}</text>
        </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
}

export default MapPin;
